import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useState } from "react";

import noop from "lodash/noop";

import type { ModalDataSchema } from "../../components/Topics/Modal/types";
import { ModalFlow } from "../../components/Topics/Modal/types";
import type { ShortTopicSchema, TopicWithKeywordsSchema } from "../../components/Topics/types";
import { activationTracking } from "../../services/tracking";

type ProviderSchema = {
    createdTopicsLength: number;
    initialModalData?: ModalDataSchema;
};
interface CollisionResolver {
    (payload: string[]): void | Promise<void>;
    (payload: TopicWithKeywordsSchema[]): void | Promise<void>;
    (payload: ShortTopicSchema[]): void | Promise<void>;
}

type ModalDataPropsSchema = {
    topicId?: string;
    isShared?: boolean;
    topicsNumber?: number;
    collisionList?: string[];
    onSubmit?: CollisionResolver;
};

interface OnCollision {
    (collisionList: string[], topicsNumber: number, onSubmit: (payload: string[]) => void | Promise<void>): void;
    (
        collisionList: string[],
        topicsNumber: number,
        onSubmit: (payload: TopicWithKeywordsSchema[]) => void | Promise<void>,
    ): void;
    (
        collisionList: string[],
        topicsNumber: number,
        onSubmit: (payload: ShortTopicSchema[]) => void | Promise<void>,
    ): void;
}

export type TopicsModalContextSchema = {
    modalData: ModalDataSchema<ModalDataPropsSchema> | null;
    isOpen: boolean;
    onClose: () => void;
    onCreateTopic: () => void;
    onCollision: OnCollision;
    onAdvanceMenuClickBack: () => void;
    onAdvanceMenuClick: () => void;
    onDeleteMenuClick: (topicId: string, isShared: boolean, modalFlow: ModalFlow) => void;
    onEditMenuClick: (topicId: string) => void;
};

const initialState: TopicsModalContextSchema = {
    modalData: null,
    isOpen: false,
    onClose: noop,
    onCollision: noop,
    onCreateTopic: noop,
    onAdvanceMenuClickBack: noop,
    onAdvanceMenuClick: noop,
    onDeleteMenuClick: noop,
    onEditMenuClick: noop,
};

export const TopicsModalContext = createContext(initialState);

export const TopicsModalContextProvider = ({
    children,
    createdTopicsLength,
    initialModalData = null,
}: PropsWithChildren<ProviderSchema>) => {
    const [modalData, setModalData] = useState<ModalDataSchema<ModalDataPropsSchema> | null>(initialModalData);
    const track = activationTracking.home;

    const onClose = useCallback(() => setModalData(null), []);

    const onAdvanceMenuClickBack = useCallback(() => {
        setModalData({ modalFlow: ModalFlow.Add });
    }, []);

    const onDeleteMenuClick = (topicId: string, isShared: boolean, modalFlow: ModalFlow) => {
        setModalData({ modalFlow, data: { topicId, isShared } });
    };

    const onEditMenuClick = (topicId: string) => {
        setModalData({ modalFlow: ModalFlow.Edit, data: { topicId } });
    };

    const onAdvanceMenuClick = () => {
        setModalData({ modalFlow: ModalFlow.Advanced });
        track.trackAdvancedModalOpen();
    };

    const onCollision = (collisionList: string[], topicsNumber: number, onSubmit: CollisionResolver) => {
        setModalData({ modalFlow: ModalFlow.Collision, data: { topicsNumber, collisionList, onSubmit } });
    };

    const onCreateTopic = () => {
        setModalData({ modalFlow: ModalFlow.Add });
        track.trackTopicStartCreation(!createdTopicsLength);
    };

    return (
        <TopicsModalContext.Provider
            value={{
                modalData,
                onClose,
                onAdvanceMenuClickBack,
                onDeleteMenuClick,
                onEditMenuClick,
                onAdvanceMenuClick,
                onCreateTopic,
                onCollision,
                isOpen: !!modalData,
            }}
        >
            {children}
        </TopicsModalContext.Provider>
    );
};

export const useTopicsModalContext = () => {
    const context = useContext(TopicsModalContext);

    if (context === undefined) {
        throw new Error("useTopicsModalContext must be used within a TopicsModalContext");
    }

    return context;
};
